/**
 * Portal settings modal (ADR-0010 P7) — the single place to configure an app's
 * end-user portal, opened from the page-builder toolbar. A left sidebar splits
 * the config into three sections:
 *
 *   - **Branding** — layout, content width, theme slots, logo, login copy.
 *   - **Security** — self-signup gate (allow + which role new accounts get).
 *   - **Navigation** — show-sidebar toggle + the explicit nav menu (PortalNavEditor).
 *
 * One Save PATCHes `portal_config` + `default_role_id` together, then invalidates
 * the portal branding/nav queries so the editor's in-context shell preview and
 * any open portal re-render immediately.
 *
 * Native `<select>` is intentional here — this is a settings surface, not the
 * page-builder node inspector (whose no-native-select rule doesn't apply).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Menu, Palette, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { PortalNavEditor, type NavEditorPage } from "@/components/apps/PortalNavEditor";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { appsApi, type AppDetail, type PortalConfig } from "@/lib/api-apps";
import { PORTAL_PREFIX } from "@/lib/portalAuth";
import { PORTAL_LAYOUTS, getLayout } from "@/lib/portalLayouts";

const SELECT_CLS = "block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm";

type Section = "branding" | "security" | "navigation";

function emptyCfg(app: AppDetail): PortalConfig {
  const pc = app.portal_config;
  return {
    layout: pc?.layout || "standard",
    theme: { ...(pc?.theme ?? {}) },
    logo_url: pc?.logo_url ?? "",
    login: { title: pc?.login?.title ?? "", subtitle: pc?.login?.subtitle ?? "" },
    show_nav: pc?.show_nav ?? true,
    content_width: pc?.content_width === "full" ? "full" : "contained",
    nav: pc?.nav ?? [],
  };
}

export function PortalSettingsModal({
  appId,
  app,
  pages,
  open,
  onOpenChange,
  inline = false,
}: {
  appId: string;
  app: AppDetail;
  pages: NavEditorPage[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** WS-4b — render the body INLINE (no Dialog chrome) as a main-pane context
   *  in the page builder, instead of a modal. The caller mounts it only when
   *  the Settings tab is active, so `open` can be passed `true`. */
  inline?: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const rolesQ = useQuery({
    queryKey: ["apps", appId, "roles"],
    queryFn: () => appsApi.listRoles(appId),
    enabled: open,
  });

  const [section, setSection] = useState<Section>("branding");
  const [cfg, setCfg] = useState<PortalConfig>(() => emptyCfg(app));
  const [signupRoleId, setSignupRoleId] = useState<string | null>(app.default_role_id ?? null);
  // Re-seed whenever the modal (re)opens, so it reflects the latest saved state.
  useEffect(() => {
    if (open) {
      setCfg(emptyCfg(app));
      setSignupRoleId(app.default_role_id ?? null);
      setSection("branding");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = useMutation({
    mutationFn: () => appsApi.update(appId, { portal_config: cfg, default_role_id: signupRoleId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "detail"] });
      qc.invalidateQueries({ queryKey: ["apps", appId] });
      qc.invalidateQueries({ queryKey: ["portal-nav", app.slug] });
      qc.invalidateQueries({ queryKey: ["portal-branding", app.slug] });
      onOpenChange(false);
    },
  });

  const set = (patch: Partial<PortalConfig>) => setCfg((c) => ({ ...c, ...patch }));
  const setColor = (key: string, v: string) => {
    const theme = { ...cfg.theme };
    if (v) theme[key] = v;
    else delete theme[key];
    set({ theme });
  };
  const layout = getLayout(cfg.layout);

  const NAV: { key: Section; label: string; Icon: typeof Palette }[] = [
    { key: "branding", label: t("apps.brandingSection", "Branding"), Icon: Palette },
    { key: "security", label: t("apps.securitySection", "Security"), Icon: ShieldCheck },
    { key: "navigation", label: t("apps.navigation", "Navigation"), Icon: Menu },
  ];

  const titleText = t("apps.portalSettings", "Portal & branding");
  const inner = (
    <>
      <DialogHeader>
        {inline ? (
          <h2 className="text-base font-semibold">{titleText}</h2>
        ) : (
          <DialogTitle>{titleText}</DialogTitle>
        )}
      </DialogHeader>
      <DialogBody className="min-h-0">
          <div className="grid h-full min-h-0 grid-cols-[180px_1fr] gap-4">
            {/* Sidebar */}
            <nav className="space-y-1 overflow-y-auto border-r border-border/60 pr-2">
              {NAV.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSection(key)}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                    section === key
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:bg-surface hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                  {label}
                </button>
              ))}
              <a
                href={`${PORTAL_PREFIX}/${app.slug}`}
                target="_blank"
                rel="noreferrer"
                className="mt-2 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-muted-foreground hover:text-foreground"
              >
                <ExternalLink className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
                {t("apps.openHomepage", "Open homepage")}
              </a>
            </nav>

            {/* Pane — scrolls on its own so the modal height stays constant. */}
            <div className="min-h-0 min-w-0 space-y-6 overflow-y-auto pr-1">
              {section === "branding" && (
                <>
                  <section className="space-y-1.5">
                    <Label htmlFor="ps-layout">{t("apps.layout", "Layout")}</Label>
                    <select
                      id="ps-layout"
                      className={SELECT_CLS}
                      value={cfg.layout}
                      onChange={(e) => set({ layout: e.target.value })}
                    >
                      {PORTAL_LAYOUTS.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.label}
                        </option>
                      ))}
                    </select>
                  </section>

                  <section className="space-y-1.5">
                    <Label htmlFor="ps-cwidth">{t("apps.contentWidth", "Content width")}</Label>
                    <select
                      id="ps-cwidth"
                      className={SELECT_CLS}
                      value={cfg.content_width ?? "contained"}
                      onChange={(e) => set({ content_width: e.target.value })}
                    >
                      <option value="contained">{t("apps.contentContained", "Contained (centered, max 1024px)")}</option>
                      <option value="full">{t("apps.contentFull", "Full width (edge-to-edge)")}</option>
                    </select>
                  </section>

                  <section className="space-y-3">
                    <h3 className="text-sm font-semibold text-muted-foreground">{t("apps.theme", "Theme")}</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {layout.slots.map((s) => (
                        <ColorField
                          key={s.key}
                          label={s.label}
                          value={cfg.theme[s.key] ?? ""}
                          onChange={(v) => setColor(s.key, v)}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t("apps.themeHint", "Leave blank to use the default. Fields adapt to the chosen layout.")}
                    </p>
                  </section>

                  <section className="space-y-1.5">
                    <Label htmlFor="ps-radius">{t("apps.cornerRadius", "Corner radius")}</Label>
                    {/* Writes `theme.radius` (a free-form CSS length the shell maps
                        to `--portal-radius`). Blank = the flat 6px default. */}
                    <select
                      id="ps-radius"
                      className={SELECT_CLS}
                      value={
                        cfg.theme.radius === "0"
                          ? "0"
                          : cfg.theme.radius === "12px"
                            ? "12px"
                            : ""
                      }
                      onChange={(e) => setColor("radius", e.target.value)}
                    >
                      <option value="0">{t("apps.radiusSharp", "Sharp (square corners)")}</option>
                      <option value="">{t("apps.radiusSubtle", "Subtle (default, 6px)")}</option>
                      <option value="12px">{t("apps.radiusRounded", "Rounded (12px)")}</option>
                    </select>
                  </section>

                  <section className="space-y-1.5">
                    <Label htmlFor="ps-logo">{t("apps.logoUrl", "Logo URL")}</Label>
                    <Input
                      id="ps-logo"
                      value={cfg.logo_url ?? ""}
                      placeholder="https://…/logo.svg"
                      onChange={(e) => set({ logo_url: e.target.value })}
                    />
                  </section>

                  <section className="space-y-3">
                    <h3 className="text-sm font-semibold text-muted-foreground">{t("apps.loginScreen", "Login screen")}</h3>
                    <div className="space-y-1.5">
                      <Label htmlFor="ps-ltitle">{t("apps.loginTitle", "Title")}</Label>
                      <Input
                        id="ps-ltitle"
                        value={cfg.login.title ?? ""}
                        placeholder={t("apps.signIn", "Sign in")}
                        onChange={(e) => set({ login: { ...cfg.login, title: e.target.value } })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="ps-lsub">{t("apps.loginSubtitle", "Subtitle")}</Label>
                      <Input
                        id="ps-lsub"
                        value={cfg.login.subtitle ?? ""}
                        onChange={(e) => set({ login: { ...cfg.login, subtitle: e.target.value } })}
                      />
                    </div>
                  </section>
                </>
              )}

              {section === "security" && (
                <section className="space-y-3">
                  <h3 className="text-sm font-semibold text-muted-foreground">{t("apps.signup", "Self-signup")}</h3>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={signupRoleId !== null}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const roles = rolesQ.data ?? [];
                          const first = roles.find((r) => r.is_builtin) ?? roles[0];
                          setSignupRoleId(first ? first.id : "");
                        } else {
                          setSignupRoleId(null);
                        }
                      }}
                    />
                    {t("apps.signupAllow", "Allow visitors to create their own account")}
                  </label>
                  {signupRoleId !== null && (
                    <div className="space-y-1.5 pl-6">
                      <Label htmlFor="ps-signup-role">{t("apps.signupRole", "Role for new sign-ups")}</Label>
                      <select
                        id="ps-signup-role"
                        className={SELECT_CLS}
                        value={signupRoleId}
                        disabled={rolesQ.isLoading}
                        onChange={(e) => setSignupRoleId(e.target.value)}
                      >
                        {signupRoleId === "" && (
                          <option value="" disabled>
                            {t("apps.signupPickRole", "Select a role…")}
                          </option>
                        )}
                        {(rolesQ.data ?? []).map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground">
                        {t("apps.signupRoleHint", "New accounts created from the portal are granted this role.")}
                      </p>
                    </div>
                  )}
                </section>
              )}

              {section === "navigation" && (
                <section className="space-y-3">
                  <p className="text-xs leading-snug text-muted-foreground">
                    {t(
                      "apps.navPanelHint",
                      "Build the portal sidebar: order pages, add external links, group items. Nothing shows unless you add it here.",
                    )}
                  </p>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={cfg.show_nav}
                      onChange={(e) => set({ show_nav: e.target.checked })}
                    />
                    {t("apps.showNav", "Show the page navigation sidebar")}
                  </label>
                  <PortalNavEditor
                    value={cfg.nav ?? []}
                    pages={pages}
                    disabled={false}
                    onChange={(nav) => set({ nav })}
                  />
                </section>
              )}
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          {save.isError && (
            <Alert tone="danger" title={t("apps.portalSaveFailed", "Could not save")} className="mr-auto">
              <span className="font-mono text-xs">
                {save.error instanceof ApiError ? save.error.code : String(save.error)}
              </span>
            </Alert>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || signupRoleId === ""}>
            {save.isPending ? t("common.saving", "Saving…") : t("common.save", "Save")}
          </Button>
        </DialogFooter>
    </>
  );

  // Inline (the builder's Impostazioni AREA): no outer padding — fit the content
  // pane edge-to-edge, like the other rail areas.
  if (inline) return <div className="flex h-full min-h-0 flex-col">{inner}</div>;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Fixed height (not h-auto) so switching sections never resizes the modal
          — only the right pane scrolls. Wider for the 2-column theme. */}
      <DialogContent className="sm:h-[80vh] sm:max-w-4xl">{inner}</DialogContent>
    </Dialog>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-1">
        <input
          type="color"
          aria-label={label}
          className="h-8 w-8 shrink-0 rounded border border-border bg-background"
          value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000"}
          onChange={(e) => onChange(e.target.value)}
        />
        <Input value={value} placeholder="#…" className="font-mono text-xs" onChange={(e) => onChange(e.target.value)} />
      </div>
    </div>
  );
}
